import ChatbotWidget from "./components/ChatbotWidget";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useAuth } from "./AuthContext";

import PageNotFound from "./pages/PageNotFound";
import AnalyticsPageView from "./components/AnalyticsPageView";
import ReferralVisitTracker from "./components/ReferralVisitTracker";
import OrgPasswordLoginPage from "./pages/OrgPasswordLoginPage";
import GtmBlueprintLoadingShell from "./components/GtmBlueprintLoadingShell";
import { RouteLoadBoundary, RouteLoadingFallback } from "./components/RouteLoadBoundary";
import { getApiContext } from "./api/requestAuth";
import { applyHistorySwipeSetting, isHistorySwipeDisabled } from "./utils/historySwipe";

const SignupPage = lazy(() => import("./pages/Signup"));
const OrgHomeUpdatePassword = lazy(() => import("./pages/OrgHomeUpdatePassword"));
const InvitedUserSetPassword = lazy(() => import("./pages/InvitedUserSetPassword"));
const OrgHomeAuthCallback = lazy(() => import("./pages/OrgHomeAuthCallback"));
const OrgHomeLandingPage = lazy(() => import("./pages/OrgHomeLandingPage"));
const OrgHomeContactPage = lazy(() => import("./pages/OrgHomeContactPage"));
const WorkFlowPageBusunessUnits = lazy(() => import("./pages/WorkFlowPageBusunessUnits"));
const WorkFlowPageOfferings = lazy(() => import("./pages/WorkFlowPageOfferings"));
const WorkFlowPageMarketing = lazy(() => import("./pages/WorkFlowPageMarketing"));
const WorkFlowPageContent = lazy(() => import("./pages/WorkFlowPageContent"));
const WorkFlowPageCampaign = lazy(() => import("./pages/WorkFlowPageCampaign"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const WorkFlowPageCampaignOnly = lazy(() => import("./pages/WorkFlowPageCampaignOnly"));
const WorkFlowPageCampaignApprover = lazy(() => import("./pages/approver/WorkFlowPageCampaignApprover"));
const WorkFlowPage = lazy(() => import("./pages/WorkFlowPage"));
const WorkFlowPageAddUser = lazy(() => import("./pages/WorkFlowPageAddUser"));
const WorkFlowPageActivities = lazy(() => import("./pages/WorkFlowPageActivities"));
const WorkFlowPageKnowledgeLibrary = lazy(() => import("./pages/WorkFlowPageKnowledgeLibrary"));
const WorkFlowPageMarketingIntegrations = lazy(() => import("./pages/WorkFlowPageMarketingIntegrations"));

const GtmBlueprintPage = lazy(() => import("./pages/GtmBlueprintPage"));
const ConsultantApplyPage = lazy(() => import("./pages/ConsultantApplyPage"));

const AdminUpgradePage = lazy(() => import("./pages/AdminUpgradePage"));
const IndustryIntelligencePage = lazy(() =>
  import("./pages/IndustryIntelligencePage")
);
const OrgCatalogPage = lazy(() => import("./pages/OrgCatalogPage"));
const OrgPromoCodesPage = lazy(() => import("./pages/OrgPromoCodesPage"));
const CreditWalletPage = lazy(() => import("./pages/CreditWalletPage"));
const OrganizationManagementPage = lazy(() => import("./pages/OrganizationManagementPage"));
const OrganizationDetails = lazy(() => import("./pages/OrganizationDetails"));
const BrandGuidelinesPage = lazy(() => import("./pages/BrandGuidelinesPage"));
const AuthCallback = lazy(() => import("./AuthCallback"));
const SocialUserLoginPage = lazy(() => import("./pages/SocialUserLoginPage"));
const DemoVideoPage = lazy(() => import("./pages/DemoVideoPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const MetaDataDeletionPage = lazy(() => import("./pages/MetaDataDeletionPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const ProspectHunterPage = lazy(() => import("./pages/ProspectHunterPage"));
const ProspectDetailPage = lazy(() => import("./pages/ProspectDetailPage"));
const DemoBookerPage = lazy(() => import("./pages/DemoBookerPage"));
const PublicDemoBookingPage = lazy(() => import("./pages/PublicDemoBookingPage"));
const GtmBlueprintLandingPage = lazy(() => import("./pages/GtmBlueprintLandingPage"));
export default function App() {
  const REACT_APP_AZURE_AD_CLIENT_ID = process.env.REACT_APP_AZURE_AD_CLIENT_ID;
  const trackingToken = window.location.pathname.match(/^\/t\/([A-Za-z0-9_-]+)\/?$/)?.[1];

  useEffect(() => {
    return applyHistorySwipeSetting(
      document.documentElement,
      isHistorySwipeDisabled(process.env.REACT_APP_DISABLE_HISTORY_SWIPE)
    );
  }, []);

  useEffect(() => {
    // Keep the complete static homepage visible for guests. Other public routes
    // are replaced by their interactive SPA pages once the app mounts.
    if (window.location.pathname !== "/") {
      document.getElementById("prerender-content")?.remove();
    }
  }, []);

  useEffect(() => {
    if (!trackingToken) return;
    const { apiUrl } = getApiContext();
    if (apiUrl) window.location.replace(`${apiUrl.replace(/\/$/, "")}/t/${trackingToken}`);
  }, [trackingToken]);

  if (trackingToken) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-slate-600">Redirecting…</div>;
  }

  console.log(
    "App REACT_APP_AZURE_AD_CLIENT_ID ",
    REACT_APP_AZURE_AD_CLIENT_ID
  );

  return (
    <>
      <div className={window.location.pathname === "/" ? "" : "min-h-screen"}>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <ChatbotWidget />
          <AnalyticsPageView />
          <ReferralVisitTracker />
          <RouteLoadBoundary>
            <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
            {/* <Route path="/landing" element={<LandingPage />} /> */}
            <Route path="/auth" element={<AuthCallback />} />
            <Route
              path="/orgpasswordlogin"
              element={<OrgPasswordLoginPage />}
            />
            <Route path="/invite/set-password" element={<InvitedUserSetPassword />} />
            <Route path="/watch-demo" element={<DemoVideoPage />} />
            <Route path="/book-demo/:token" element={<PublicDemoBookingPage />} />
            <Route path="/prospect-hunter" element={<ProspectHunterPage />} />
            <Route path="/prospect-hunter/:id" element={<ProspectDetailPage />} />
            <Route path="/demo-booker" element={<DemoBookerPage />} />
            <Route path="/gtm-blueprint" element={<GtmBlueprintLandingPage />} />
            {/* <Route
            path="/userlogin"
            element={<UserLoginPage  />}
          />               */}
            <Route path="/socialuserlogin" element={<SocialUserLoginPage />} />
            {/* <Route path="/signup" element={<SignupPage />} /> */}
            <Route
              path="/signup"
              element={<OrgPasswordLoginPage />}
            />            
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/meta-data-deletion" element={<MetaDataDeletionPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/pricing" element={<AdminUpgradePage />} />
            {/* <Route path="/" element={<OrgHomeLandingPage/>} /> */}

            <Route
              path="/home/updatepassword"
              element={<OrgHomeUpdatePassword />}
            />
            <Route
              path="/home/auth/callback"
              element={<OrgHomeAuthCallback />}
            />
            <Route path="/home/organization" element={<OrgHomeLandingPage />} />
            <Route
              path="/home/organization/contact"
              element={<OrgHomeContactPage />}
            />
            <Route path="/home/organization/details" element={<OrganizationDetails />} />
            <Route path="/home/organization/brand-guidelines" element={<BrandGuidelinesPage />} />
            <Route
              path="/industry-intelligence"
              element={<IndustryIntelligencePage />}
            />
            <Route path="/credit-catalog" element={<OrgCatalogPage />} />
            <Route path="/promo-campaigns" element={<OrgPromoCodesPage />} />
            <Route path="/credits" element={<CreditWalletPage />} />
            <Route path="/organization-management" element={<OrganizationManagementPage />} />
            <Route
              path="/businessunits"
              element={<WorkFlowPageBusunessUnits />}
            />
            <Route path="/workflow" element={<WorkFlowPage />} />
            <Route path="/offerings" element={<WorkFlowPageOfferings />} />
            <Route path="/marketing" element={<WorkFlowPageMarketing />} />

            <Route path="/content" element={<WorkFlowPageContent />} />
            <Route path="/campaign" element={<WorkFlowPageCampaign />} />
            <Route
              path="/home/campaign"
              element={<WorkFlowPageCampaignOnly />}
            />
            <Route
              path="/dashboard"
              element={<WorkFlowPageCampaignApprover />}
            />
            <Route path="/" element={<AuthenticatedHomeRoute />} />
            <Route path="/adduser" element={<WorkFlowPageAddUser />} />
            <Route path="/activities" element={<WorkFlowPageActivities />} />
            <Route
              path="/knowledgelibrary"
              element={<WorkFlowPageKnowledgeLibrary />}
            />
            <Route
              path="/marketingintegrations"
              element={<WorkFlowPageMarketingIntegrations />}
            />
            
            
            <Route
              path="/create-your-gtm-blueprint"
              element={
                <Suspense fallback={<GtmBlueprintLoadingShell />}>
                  <GtmBlueprintPage />
                </Suspense>
              }
            />
            <Route path="/become-consultant" element={<ConsultantApplyPage />} />
            <Route path="/admin/upgrade" element={<AdminUpgradePage />} />

            <Route path="*" element={<PageNotFound />} />
            </Routes>
            </Suspense>
          </RouteLoadBoundary>
        </BrowserRouter>
      </div>
    </>
  );
}

function AuthenticatedHomeRoute() {
  const { authReady } = useAuth();

  useEffect(() => {
    if (process.env.NODE_ENV === "production" && authReady) {
      document.getElementById("prerender-content")?.remove();
    }
  }, [authReady]);

  // The prerender script only runs for production builds. The development
  // server serves public/index.html directly, so it needs the React homepage.
  if (process.env.NODE_ENV !== "production") {
    return <OrgHomeLandingPage />;
  }

  // Keep the prerendered homepage visible while auth restores, then mount the
  // interactive guest homepage or the authenticated onboarding flow.
  return authReady ? <OrgHomeLandingPage /> : null;
}
